import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Receipt, Loader2, RotateCcw, Send, ChevronDown,
    CheckCircle, Navigation, ListChecks, Table2, Building2,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';
import { formatIndianCurrency } from '../../utilities/amountToTextHelper';

import {
    fetchRequestItems,
    fetchAssetSaleItemRecDetails,
    fetchAssetPartyDetails,
    fetchBankDetails,
    saveAssetSaleReceiptData,
    clearItemGrid,
    clearPartyDetails,
    clearSaveResult,
    resetAssetSaleReceipt,
    selectRequestItemsArray,
    selectRequestItemsLoading,
    selectItemGridArray,
    selectItemGridLoading,
    selectPartyDetails,
    selectBankListArray,
    selectBankListLoading,
    selectSaveStatus,
    selectSaveLoading,
    selectSaveError,
} from '../../slices/purchaseSlice/assetSaleReceiptSlice';

// ── Constants ─────────────────────────────────────────────────────────────────

const MODE_OPTIONS = ['Cash', 'RTGS/E-Trans', 'DD'];
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatDateForAPI = (val) => {
    if (!val) return '';
    if (typeof val === 'string' && /^\d{2}-[A-Za-z]{3}-\d{4}$/.test(val)) return val;
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
        const [yyyy, mm, dd] = val.split('T')[0].split('-');
        return `${dd}-${MONTH_ABBR[parseInt(mm, 10) - 1]}-${yyyy}`;
    }
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

// ── Shared UI ─────────────────────────────────────────────────────────────────

const inputCls =
    'w-full px-3.5 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 hover:border-gray-300 transition-all disabled:opacity-60 disabled:cursor-not-allowed';

const Label = ({ children, required }) => (
    <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1.5">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectIcon = ({ loading }) => (
    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
        {loading
            ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" />
            : <ChevronDown className="h-4 w-4 text-gray-400" />}
    </div>
);

const SectionHeader = ({ icon: Icon, title, subtitle, action }) => (
    <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/60 dark:bg-gray-900/40 rounded-t-2xl">
        <div className="flex items-center gap-3">
            <Icon className="h-4 w-4 text-indigo-500" />
            <div>
                <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100">{title}</h2>
                {subtitle && <p className="text-xs text-gray-400 dark:text-gray-500">{subtitle}</p>}
            </div>
        </div>
        {action}
    </div>
);

// ── Component ─────────────────────────────────────────────────────────────────

const AssetSaleReceipt = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);

    const userId = userData?.userId || userData?.UID || userData?.employeeId || '';
    const roleId = userData?.roleId || userData?.RID || 0;
    const userName = userData?.userName || userData?.UserName || 'system';

    // ── Redux ──────────────────────────────────────────────────────────────
    const requestItems = useSelector(selectRequestItemsArray);
    const requestItemsLoading = useSelector(selectRequestItemsLoading);
    const itemGrid = useSelector(selectItemGridArray);
    const itemGridLoading = useSelector(selectItemGridLoading);
    const partyDetails = useSelector(selectPartyDetails);
    const bankList = useSelector(selectBankListArray);
    const bankLoading = useSelector(selectBankListLoading);
    const saveStatus = useSelector(selectSaveStatus);
    const saveLoading = useSelector(selectSaveLoading);
    const saveError = useSelector(selectSaveError);

    // ── Form state ────────────────────────────────────────────────────────
    const [requestId, setRequestId] = useState('');
    const [viewed, setViewed] = useState(false);
    const [checkedRows, setCheckedRows] = useState({}); // { [IId]: boolean }
    const [fromBank, setFromBank] = useState('');
    const [modeOfPay, setModeOfPay] = useState('');
    const [no, setNo] = useState('');
    const [paymentDate, setPaymentDate] = useState(new Date());
    const [bankRemarks, setBankRemarks] = useState('');
    const [userRemarks, setUserRemarks] = useState('');
    const [amount, setAmount] = useState('');

    // ── Init ──────────────────────────────────────────────────────────────
    useEffect(() => {
        if (userId) {
            dispatch(fetchRequestItems({ uid: userId, rid: roleId }));
        }
        dispatch(fetchBankDetails());
        return () => {
            dispatch(resetAssetSaleReceipt());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // Save result effect
    useEffect(() => {
        if (saveStatus === 'success') {
            const [, transNo] = (saveError || '').split(',');
            toast.success(`Transaction No ${transNo || ''} Generated Successfully`);
            dispatch(clearSaveResult());
            handleFullReset();
        } else if (saveStatus === 'failed' && saveError) {
            toast.error(saveError);
            dispatch(clearSaveResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveStatus]);

    // ── Handlers ──────────────────────────────────────────────────────────
    const handleFullReset = () => {
        setRequestId(''); setViewed(false); setCheckedRows({});
        setFromBank(''); setModeOfPay(''); setNo('');
        setPaymentDate(new Date()); setBankRemarks(''); setUserRemarks(''); setAmount('');
        dispatch(resetAssetSaleReceipt());
        if (userId) {
            dispatch(fetchRequestItems({ uid: userId, rid: roleId }));
        }
        dispatch(fetchBankDetails());
    };

    const handleView = () => {
        if (!requestId) { toast.warn('Select Request No.'); return; }
        dispatch(fetchAssetSaleItemRecDetails(requestId));
        dispatch(fetchAssetPartyDetails(requestId));
        setViewed(true);
        setCheckedRows({});
    };

    const toggleCheck = (iId) => {
        setCheckedRows((prev) => ({ ...prev, [iId]: !prev[iId] }));
    };

    const balance = partyDetails?.Balance || 0;

    const handleAmountChange = (e) => {
        const val = e.target.value;
        if (val !== '' && !/^\d*\.?\d{0,2}$/.test(val)) return;
        const numVal = parseFloat(val);
        if (val !== '' && !isNaN(numVal) && numVal > balance) {
            toast.error('Amount is not more than Balance Amount');
            setAmount('');
            return;
        }
        setAmount(val);
    };

    const allChecked = itemGrid.length > 0 && itemGrid.every((r) => checkedRows[r.IId]);

    const handleSubmit = () => {
        if (!fromBank) { toast.warn('Select Bank.'); return; }
        if (!modeOfPay) { toast.warn('Select Mode of Pay.'); return; }
        if (!no.trim()) { toast.warn('Enter No.'); return; }
        if (!paymentDate) { toast.warn('Enter Payment Date.'); return; }
        if (!bankRemarks.trim()) { toast.warn('Enter Bank Remarks.'); return; }
        if (!userRemarks.trim()) { toast.warn('Enter User Remarks.'); return; }
        if (!amount || parseFloat(amount) === 0) { toast.warn('Enter Amount.'); return; }
        if (itemGrid.length === 0) { toast.warn('No items found for this request.'); return; }
        if (!allChecked) { toast.warn('Please Verify Item Code.'); return; }

        const codes = itemGrid.map((r) => r.ItemCode);
        if (new Set(codes).size !== codes.length) { toast.warn('Duplicate ItemCode for Asset Sale.'); return; }

        const lastRow = itemGrid[itemGrid.length - 1];

        dispatch(saveAssetSaleReceiptData({
            rid: lastRow.IId,
            fromBank,
            modeOfPay,
            amount,
            bankRemarks,
            userRemarks,
            paymentDate: formatDateForAPI(paymentDate),
            no,
            roleId,
            createdBy: userName,
        }));
    };

    // ── Derived ───────────────────────────────────────────────────────────
    const isBusy = saveLoading || requestItemsLoading || itemGridLoading;

    const showSummary = viewed && itemGrid.length > 0 && fromBank && modeOfPay &&
        no.trim() && paymentDate && bankRemarks.trim() && userRemarks.trim() && amount;
    const canSubmit = showSummary && !saveLoading;

    // ── Render ────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 md:p-6">

            {/* ── Page Header ──────────────────────────────────────────── */}
            <div className="max-w-7xl mx-auto mb-6">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-xl shadow-indigo-500/20 p-7 text-white">
                    <div className="absolute inset-0 opacity-10"
                        style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
                    <div className="absolute top-0 right-0 w-72 h-72 bg-purple-500 rounded-full -translate-y-1/2 translate-x-1/4 opacity-20 blur-3xl" />
                    <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shadow-lg border border-white/20">
                                <Receipt className="h-7 w-7 text-white" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-bold text-indigo-200 uppercase tracking-wider bg-white/10 px-2 py-0.5 rounded-full">Purchase Module</span>
                                </div>
                                <h1 className="text-2xl md:text-3xl font-black tracking-tight">
                                    {menuData?.name || 'Asset Sale Receipt'}
                                </h1>
                                <p className="text-indigo-200 text-sm mt-0.5">Record the bank receipt against an approved asset sale request</p>
                            </div>
                        </div>
                        <div className="hidden sm:flex items-center gap-3 text-indigo-200">
                            <div className="text-right">
                                <p className="text-xs uppercase tracking-wider">Module</p>
                                <p className="text-sm font-bold text-white">Purchase / Asset Sale Receipt</p>
                            </div>
                            <Navigation className="h-5 w-5 opacity-60" />
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto space-y-6">

                {/* ── Section 1: Select Request ─────────────────────────── */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <SectionHeader
                        icon={ListChecks}
                        title="Select Request No"
                        subtitle="Choose the approved asset sale request"
                        action={
                            <button
                                onClick={handleFullReset}
                                disabled={isBusy}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50"
                            >
                                <RotateCcw className="h-3.5 w-3.5" /> Reset
                            </button>
                        }
                    />
                    <div className="p-6 md:p-8">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                            <div className="md:col-span-2">
                                <Label required>Request No</Label>
                                <div className="relative">
                                    <select
                                        className={`${inputCls} appearance-none pr-10`}
                                        value={requestId}
                                        onChange={(e) => { setRequestId(e.target.value); setViewed(false); dispatch(clearItemGrid()); dispatch(clearPartyDetails()); }}
                                        disabled={requestItemsLoading || isBusy}
                                    >
                                        <option value="">{requestItemsLoading ? 'Loading…' : '-Please Select-'}</option>
                                        {requestItems.map((r) => (
                                            <option key={r.Requestid} value={r.Requestid}>{r.RequestDetails}</option>
                                        ))}
                                    </select>
                                    <SelectIcon loading={requestItemsLoading} />
                                </div>
                            </div>
                            <div>
                                <button
                                    onClick={handleView}
                                    disabled={!requestId || isBusy}
                                    className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                >
                                    {itemGridLoading
                                        ? <><Loader2 className="h-4 w-4 animate-spin" /> Loading…</>
                                        : <><ListChecks className="h-4 w-4" /> View</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Section 2: Item Grid ──────────────────────────────── */}
                {viewed && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <SectionHeader
                            icon={Table2}
                            title="Asset Sale Item"
                            subtitle="Check each row to verify it before submitting"
                        />
                        <div className="p-6 md:p-8">
                            {itemGridLoading ? (
                                <div className="flex items-center justify-center py-8 text-gray-400">
                                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
                                </div>
                            ) : itemGrid.length === 0 ? (
                                <div className="px-4 py-3 rounded-xl text-sm font-medium bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 text-center">
                                    No Data Found
                                </div>
                            ) : (
                                <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30">
                                                <th className="px-4 py-3 text-center text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Chk</th>
                                                <th className="px-4 py-3 text-left text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Request No</th>
                                                <th className="px-4 py-3 text-left text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Item Code</th>
                                                <th className="px-4 py-3 text-left text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Item Name</th>
                                                <th className="px-4 py-3 text-left text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Invoice Date</th>
                                                <th className="px-4 py-3 text-left text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Book Value Date</th>
                                                <th className="px-4 py-3 text-right text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Selling Amount</th>
                                                <th className="px-4 py-3 text-right text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Balance Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {itemGrid.map((r, idx) => (
                                                <tr key={r.IId} className={`border-t border-gray-100 dark:border-gray-700 ${idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/50 dark:bg-gray-700/30'}`}>
                                                    <td className="px-4 py-3 text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={!!checkedRows[r.IId]}
                                                            onChange={() => toggleCheck(r.IId)}
                                                            className="accent-indigo-600 h-4 w-4"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-800 dark:text-gray-100">{r.Requestno}</td>
                                                    <td className="px-4 py-3 text-gray-800 dark:text-gray-100">{r.ItemCode}</td>
                                                    <td className="px-4 py-3 text-gray-800 dark:text-gray-100 font-medium">{r.ItemName}</td>
                                                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{r.InvoiceDate}</td>
                                                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{r.BookValueDate}</td>
                                                    <td className="px-4 py-3 text-right font-semibold text-gray-800 dark:text-gray-100">{formatIndianCurrency(r.SellingAmount)}</td>
                                                    <td className="px-4 py-3 text-right font-semibold text-gray-800 dark:text-gray-100">{formatIndianCurrency(r.BalanceAmount)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ── Section 3: Receipt Details ────────────────────────── */}
                {viewed && itemGrid.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <SectionHeader
                            icon={Building2}
                            title="Receipt Details"
                            subtitle="Buyer, bank, mode of pay and amount"
                        />
                        <div className="p-6 md:p-8">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                <div>
                                    <Label>Buyer Name</Label>
                                    <input type="text" readOnly value={partyDetails?.PartyName || ''} className={inputCls} />
                                </div>
                                <div className="md:col-span-2">
                                    <Label>Buyer Address</Label>
                                    <input type="text" readOnly value={partyDetails?.PartyAddress || ''} className={inputCls} />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                <div>
                                    <Label required>From Bank</Label>
                                    <div className="relative">
                                        <select
                                            className={`${inputCls} appearance-none pr-10`}
                                            value={fromBank}
                                            onChange={(e) => setFromBank(e.target.value)}
                                            disabled={bankLoading || isBusy}
                                        >
                                            <option value="">{bankLoading ? 'Loading…' : '-Please Select-'}</option>
                                            {bankList.map((b) => (
                                                <option key={b.Bank_Id} value={b.Bank_Id}>{b.Bank_Name}</option>
                                            ))}
                                        </select>
                                        <SelectIcon loading={bankLoading} />
                                    </div>
                                </div>
                                <div>
                                    <Label required>Mode of Pay</Label>
                                    <div className="relative">
                                        <select
                                            className={`${inputCls} appearance-none pr-10`}
                                            value={modeOfPay}
                                            onChange={(e) => setModeOfPay(e.target.value)}
                                            disabled={isBusy}
                                        >
                                            <option value="">-Please Select-</option>
                                            {MODE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                                        </select>
                                        <SelectIcon loading={false} />
                                    </div>
                                </div>
                                <div>
                                    <Label required>No</Label>
                                    <input
                                        type="text"
                                        className={inputCls}
                                        placeholder="Enter reference no"
                                        value={no}
                                        onChange={(e) => setNo(e.target.value)}
                                        disabled={isBusy}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                <div>
                                    <Label required>Payment Date</Label>
                                    <CustomDatePicker
                                        value={paymentDate}
                                        onChange={setPaymentDate}
                                        disabled={isBusy}
                                        maxDate={new Date()}
                                    />
                                </div>
                                <div>
                                    <Label required>Amount (₹)</Label>
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        placeholder="0.00"
                                        className={inputCls}
                                        value={amount}
                                        onChange={handleAmountChange}
                                        disabled={isBusy}
                                    />
                                    {balance > 0 && (
                                        <p className="text-[10px] text-gray-400 mt-1">Balance: ₹ {formatIndianCurrency(balance)}</p>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <Label required>Bank Remarks</Label>
                                    <textarea
                                        rows={3}
                                        placeholder="Enter bank remarks…"
                                        value={bankRemarks}
                                        onChange={(e) => setBankRemarks(e.target.value)}
                                        disabled={isBusy}
                                        className={`${inputCls} resize-none`}
                                    />
                                </div>
                                <div>
                                    <Label required>User Remarks</Label>
                                    <textarea
                                        rows={3}
                                        placeholder="Enter remarks…"
                                        value={userRemarks}
                                        onChange={(e) => setUserRemarks(e.target.value)}
                                        disabled={isBusy}
                                        className={`${inputCls} resize-none`}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Summary ───────────────────────────────────────────── */}
                {showSummary && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <SectionHeader icon={CheckCircle} title="Receipt Summary" subtitle="Review before submitting" />
                        <div className="p-6 md:p-8">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                                {[
                                    { label: 'Buyer', value: partyDetails?.PartyName },
                                    { label: 'Bank', value: bankList.find((b) => String(b.Bank_Id) === String(fromBank))?.Bank_Name },
                                    { label: 'Mode of Pay', value: modeOfPay },
                                    { label: 'No', value: no },
                                    { label: 'Payment Date', value: formatDateForAPI(paymentDate) },
                                    { label: 'Amount', value: `₹ ${formatIndianCurrency(parseFloat(amount) || 0)}` },
                                    { label: 'Created By', value: userName },
                                    { label: 'Bank Remarks', value: bankRemarks, wide: true },
                                    { label: 'User Remarks', value: userRemarks, wide: true },
                                ].map(({ label, value, wide }) => (
                                    <div key={label} className={`bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 ${wide ? 'md:col-span-3' : ''}`}>
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
                                        <p className="font-semibold text-gray-800 dark:text-gray-100 break-words">{value ?? '—'}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Action Buttons ────────────────────────────────────── */}
                <div className="flex items-center justify-end gap-3 pb-6">
                    <button
                        onClick={handleFullReset}
                        disabled={isBusy}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50"
                    >
                        <RotateCcw className="h-4 w-4" /> Reset
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-500/30 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                        {saveLoading
                            ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</>
                            : <><Send className="h-4 w-4" /> Submit</>}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default AssetSaleReceipt;
